import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { useFetchWrapper } from "../../../hooks/useFetchWrapper";
import { useAPIErrorContext } from '../../../contexts/apiErrorContext'
import { FormHeading } from "../../Shared/Forms/FormHeading";
import { clientConfig } from "../../../utilities/configService";


interface IFleetProps {
    fleetID: string,
    fleetName: string,
    vehicleSetID: number
}

interface IVehicleSet {
    fleetVehicleSetID: number,
    vehicleSetName: string,
    vehicleTypeID: number,
    vehicleTypeName: string,
    vehicleSecondaryTypeID: number,
    vehicleSecondaryTypeName: string,
    vehicleCount: number,
    fuelTypeID: number,
    fuelTypeName: string,
    usageValue: number,
    usageValueUnits: string,
    chargeHours: number
}

interface IVehicleType {
    vehicleTypeID: number,
    vehicleTypeName: string
    vehicleSecondaryTypes: IVehicleSecondaryType[]
}

interface IVehicleSecondaryType {
    vehicleSecondaryTypeID: number,
    vehicleSecondaryTypeName: string
}

interface IVehicleAssumptions {
    fuelTypeID: number,
    fuelTypeName: string,
    avgAnnualUsage: number
    annualUseUnits: string
}

export const EditVehicleSet = ({ fleetID, fleetName, vehicleSetID }: IFleetProps) => {
    const fetchWrapper = useFetchWrapper();
    const { addError, clearError } = useAPIErrorContext();
    const navigate = useNavigate();
    const applicationName = clientConfig?.APPLICATION_NAME || '';

    const [vehicleSetToEdit, setVehicleSetToEdit] = useState<IVehicleSet>();
    const [currentVehicleSets, setCurrentVehicleSets] = useState<IVehicleSet[]>([]);

    const [vehicleTypes, setVehicleTypes] = useState<IVehicleType[]>([]);
    const [vehicleSecondaryTypes, setVehicleSecondaryTypes] = useState<IVehicleSecondaryType[]>([]);
    const [vehicleAssumptions, setVehicleAssumptions] = useState<IVehicleAssumptions[]>([]);

    const [vehicleTypeSelect, setVehicleTypeSelect] = useState('');
    const [vehicleSecondaryTypeSelect, setVehicleSecondaryTypeSelect] = useState('');
    const [showSecondaryTypes, setShowSecondaryTypes] = useState(false);
    const [vehicleTypePlusSecondary, setVehicleTypePlusSecondary] = useState(0);
    const [vehicleSetName, setVehicleSetName] = useState('');
    const [vehicleCount, setVehicleCount] = useState(0);
    const [fuelTypeSelect, setFuelTypeSelect] = useState('');
    const [annualUse, setAnnualUse] = useState("0");
    const [annualUseUnits, setAnnualUseUnits] = useState('');
    const [chargeHours, setChargeHours] = useState(6);
    const [showCancelButton, setShowCancelButton] = useState(false);

    const [formErrors, setFormErrors] = useState({ vehicleType: '', vehicleSecondaryType: '', vehicleCount: '', vehicleSetName: '', fuelType: '', annualUse: '' });


    const getVehicleSetsByFleetID = (vehicleSetID: any) => {
        fetchWrapper.get(`vehicleSet/${fleetID}`)
            .then((data) => {
                if (data.success && data.vehicleSetList) {
                    setCurrentVehicleSets(data.vehicleSetList);
                    if (vehicleSetID && vehicleSetID !== 0) {
                        //filter to editable vehicleSetID and set form fields
                        data.vehicleSetList.filter((vSet: IVehicleSet) => {
                            var dataVehicleSetID = vSet.fleetVehicleSetID.toString();
                            if (dataVehicleSetID === vehicleSetID.toString()) {
                                setVehicleSetToEdit(vSet);
                                setVehicleTypeSelect(vSet.vehicleTypeID.toString());
                                setVehicleSetName(vSet.vehicleSetName);
                                setVehicleCount(vSet.vehicleCount);
                                setFuelTypeSelect(vSet.fuelTypeID.toString());
                                setAnnualUse(vSet.usageValue.toString());
                                setAnnualUseUnits(vSet.usageValueUnits);
                                setChargeHours(vSet.chargeHours);
                                return vSet;
                            }
                            return vSet;
                        })
                    }
                }
            });
    }

    const getVehicleTypes = () => {
        fetchWrapper.get('vehicleType')
            .then((data) => {
                if (data.success && data.vehicleTypes) {
                    setVehicleTypes(data.vehicleTypes);
                }
            });
    };

    const getVehicleAssumptions = () => {
        if (vehicleTypeSelect && vehicleSecondaryTypeSelect) {
            fetchWrapper.get(`vehicleAssumptions/${vehicleTypeSelect}/${vehicleSecondaryTypeSelect}`)
                .then((data) => {
                    if (data.success && data.vehicleAssumptions) {
                        setVehicleAssumptions(data.vehicleAssumptions);
                        if (vehicleTypeSelect === vehicleSetToEdit?.vehicleTypeID.toString()) {
                            setFuelTypeSelect(vehicleSetToEdit.fuelTypeID.toString());
                            setAnnualUse(vehicleSetToEdit.usageValue.toString());
                            setAnnualUseUnits(vehicleSetToEdit.usageValueUnits);
                            setChargeHours(vehicleSetToEdit.chargeHours);
                        }
                        else {
                            var defaultAssumptions = data.vehicleAssumptions[0];
                            if (defaultAssumptions) {
                                setFuelTypeSelect(defaultAssumptions.fuelTypeID.toString());
                                setAnnualUse(defaultAssumptions.avgAnnualUsage.toString());
                                setAnnualUseUnits(defaultAssumptions.annualUseUnits);
                            }
                        }
                    }
                }
                );
        }
    };

    const clearFuelAssumptions = () => {
        setFuelTypeSelect('');
        setAnnualUse('0');
        setAnnualUseUnits('');
        setChargeHours(6);
    }

    useEffect(() => {
        getVehicleTypes();
    }, [fleetID]);

    useEffect(() => {
        getVehicleSetsByFleetID(vehicleSetID);
    }, [vehicleSetID]);

    useEffect(() => {
        //trigger secondary type drop down if a secondary type exists for selected type
        var vTypeSelect = +vehicleTypeSelect;
        var vSecondTypeSelect = +vehicleSecondaryTypeSelect;
        setVehicleTypePlusSecondary(vTypeSelect + vSecondTypeSelect);
    }, [vehicleSecondaryTypeSelect]);

    useEffect(() => {
        //trigger secondary type drop down if a secondary type exists for selected type
        vehicleTypes.filter(vType => {
            if (vType.vehicleTypeID.toString() === vehicleTypeSelect) {
                if (vType.vehicleSecondaryTypes.length > 1) {
                    setShowSecondaryTypes(true);
                    setVehicleSecondaryTypes(vType.vehicleSecondaryTypes);
                    setVehicleSecondaryTypeSelect('');
                }
                else {
                    setShowSecondaryTypes(false);
                    setVehicleSecondaryTypes(vType.vehicleSecondaryTypes);
                    setVehicleSecondaryTypeSelect(vType.vehicleSecondaryTypes[0].vehicleSecondaryTypeID.toString());
                    var vTypeSelect = +vehicleTypeSelect;
                    var vSecondTypeSelect = +vehicleSecondaryTypeSelect;
                    setVehicleTypePlusSecondary(vTypeSelect + vSecondTypeSelect);
                }
            }
            return vType;
        });
        if (vehicleTypeSelect === vehicleSetToEdit?.vehicleTypeID.toString()) {
            setVehicleSecondaryTypeSelect(vehicleSetToEdit.vehicleSecondaryTypeID.toString());
        }
    }, [vehicleTypeSelect]);

    useEffect(() => {
        //trigger fuel type drop down when a secondary type is selected
        clearFuelAssumptions();
        getVehicleAssumptions();
    }, [vehicleTypePlusSecondary]);

    const errorClass = (error: string | any[]) => {
        return (error.length === 0 ? '' : 'is-invalid');
    }

    const onVehicleSetEditOrCancel = () => {
        navigate('/ViewFleets');
    }

    const onVehicleCountChange = (event: { target: { value: any; min: any; max: any; }; }) => {
        let { value, min, max } = event.target;
        value = Math.max(Number(min), Math.min(Number(max), Number(value)));
        if (!isNaN(+value)) {
            setVehicleCount(+value);
        }
    }

    const validateAndSaveEditedVehicleSet = async (e: any) => {
        e.preventDefault();
        let formIsValid = validateForm();
        if (formIsValid) {
            updateVehicleSet();
        }
    };

    const validateForm = () => {
        var fieldValidationError = '';
        var fieldErrors = {
            vehicleType: '',
            vehicleSecondaryType: '',
            vehicleCount: '',
            vehicleSetName: '',
            fuelType: '',
            annualUse: ''
        }

        if (vehicleTypeSelect.length <= 0) {
            fieldValidationError = "Vehicle Type is required.";
            fieldErrors.vehicleType = "Vehicle Type is required.";
        }
        if (vehicleSecondaryTypeSelect.length <= 0) {
            fieldValidationError = "Vehicle Subtype is required.";
            fieldErrors.vehicleSecondaryType = "Vehicle Subtype is required.";

        }
        if (isNaN(vehicleCount)) {
            fieldValidationError = "Count is required.";
            fieldErrors.vehicleCount = "Count is required.";
        }
        if (vehicleCount <= 0) {
            fieldValidationError = "Count is required.";
            fieldErrors.vehicleCount = "Count is required.";
        }
        if (vehicleCount > 999) {
            fieldValidationError = "A vehicle set is limited to 999 vehicles.";
            fieldErrors.vehicleCount = "A vehicle set is limited to 999 vehicles.";
        }
        if (vehicleSetName.length <= 0) {
            fieldValidationError = "Vehicle Set Name is required.";
            fieldErrors.vehicleSetName = "Vehicle Set Name is required.";

        }
        if (fuelTypeSelect.length <= 0) {
            fieldValidationError = "Fuel Type is required.";
            fieldErrors.fuelType = "Fuel Type is required.";

        }
        if (annualUse === '0' || annualUse.length <= 0) {
            fieldValidationError = "Annual Use per Vehicle is required.";
            fieldErrors.annualUse = "Annual Use per Vehicle is required.";

        }

        if (fieldValidationError.length > 0) {
            setFormErrors({
                vehicleType: fieldErrors.vehicleType,
                vehicleSecondaryType: fieldErrors.vehicleSecondaryType,
                vehicleCount: fieldErrors.vehicleCount,
                vehicleSetName: fieldErrors.vehicleSetName,
                fuelType: fieldErrors.fuelType,
                annualUse: fieldErrors.annualUse
            });
            console.log("formErrors state: ", formErrors);
            return false;
        }
        else {
            return true;
        }
    }

    const updateVehicleSet = async () => {
        clearError();
        var updateVehicleSetRequest = {
            fleetVehicleSetID: vehicleSetID,
            fleetID: fleetID,
            vehicleSetName: vehicleSetName,
            fuelTypeID: fuelTypeSelect,
            vehicleTypeID: vehicleTypeSelect,
            vehicleSecondaryTypeID: vehicleSecondaryTypeSelect,
            vehicleCount: vehicleCount,
            annualMilage: annualUse,
            chargeHours: chargeHours,
            applicationName: applicationName
        };
        setFormErrors({ vehicleType: '', vehicleSecondaryType: '', vehicleCount: '', vehicleSetName: '', fuelType: '', annualUse: '' });

        fetchWrapper.post('vehicleSet/update', 'application/json', updateVehicleSetRequest)
            .then((data) => {
                if (data.success) {
                    navigate('/ViewFleets');
                }
                else {
                    addError("Error submitting form", data.message);
                }
            }).catch((error: any) => {
                addError("Error submitting form", error);
            });
    };


    return (
        <Col className="">
            <Row className="contentBlock">
                <Col sm={8} className="formBody">
                    <FormHeading title={`Edit ${vehicleSetToEdit?.vehicleSetName}`} textAlign="alignCenter" light={false} />
                    <div className="fleetRequestForm formInnerBody">
                        <Row>
                            <Col sm={12}  >
                                <div className="text-align-center fieldsReq">All fields required</div>
                            </Col>
                        </Row>
                        <Form>
                            <FormGroup row className={`form-group ${errorClass(formErrors.vehicleType)}`}>
                                <Label for="vehicleType" sm={4} className="formRequired">Vehicle Type</Label>
                                <Col sm={8}>
                                    <Input className="fieldWidth300" type="select" name="select" id="vehicleType" value={vehicleTypeSelect} required onChange={e => setVehicleTypeSelect(e.target.value)} invalid={formErrors.vehicleType !== ''}>
                                        <option key="select" value="" disabled>
                                            select...
                                        </option>
                                        {vehicleTypes.map((item: IVehicleType) => {
                                            return (
                                                <option key={item.vehicleTypeID} value={item.vehicleTypeID}>
                                                    {item.vehicleTypeName}
                                                </option>)
                                        })}
                                    </Input>
                                    <FormFeedback>{formErrors.vehicleType}</FormFeedback>
                                </Col>
                            </FormGroup>
                            {showSecondaryTypes ? (
                                <FormGroup row className={`form-group ${errorClass(formErrors.vehicleSecondaryType)}`}>
                                    <Label for="vehicleSecondaryType" sm={4} className="formRequired">Vehicle Subtype</Label>
                                    <Col sm={8}>
                                        <Input className="fieldWidth300" type="select" name="select" id="vehicleSecondaryType" value={vehicleSecondaryTypeSelect} required onChange={e => setVehicleSecondaryTypeSelect(e.target.value)} invalid={formErrors.vehicleSecondaryType !== ''}>
                                            <option key="select" value="" disabled>
                                                select...
                                            </option>
                                            {vehicleSecondaryTypes.map((item: IVehicleSecondaryType) => {
                                                return (
                                                    <option key={item.vehicleSecondaryTypeID} value={item.vehicleSecondaryTypeID}>
                                                        {item.vehicleSecondaryTypeName}
                                                    </option>)
                                            })}
                                        </Input>
                                        <FormFeedback>{formErrors.vehicleSecondaryType}</FormFeedback>
                                    </Col>
                                </FormGroup>
                            )
                                : ('')}
                            <FormGroup row className={`form-group ${errorClass(formErrors.vehicleCount)}`}>
                                <Label for="vehicleCount" sm={4} className="formRequired ">Count</Label>
                                <Col sm={8}>
                                    <Input className="fieldWidth100" type="text" max="999" name="name" id="vehicleCount" value={vehicleCount} onChange={onVehicleCountChange} maxLength={128} invalid={formErrors.vehicleCount !== ''} />
                                    <FormFeedback>{formErrors.vehicleCount}</FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row className={`form-group ${errorClass(formErrors.vehicleSetName)}`}>
                                <Label for="vehicleSetName" sm={4} className="formRequired ">Vehicle Set Name</Label>
                                <Col sm={8}>
                                    <Input className="fieldWidth300" type="text" name="name" id="vehicleSetName" placeholder="Please name this vehicle set" value={vehicleSetName} onChange={e => setVehicleSetName(e.target.value)} maxLength={128} invalid={formErrors.vehicleSetName !== ''} />
                                    <FormFeedback>{formErrors.vehicleSetName}</FormFeedback>
                                </Col>
                            </FormGroup>
                            <hr />
                            <FormGroup row className={`form-group ${errorClass(formErrors.fuelType)}`}>
                                <Label for="fuelType" sm={4} className="formRequired">Fuel Type</Label>
                                <Col sm={8}>
                                    <Input className="fieldWidth300" type="select" name="select" id="fuelType" value={fuelTypeSelect} required onChange={e => setFuelTypeSelect(e.target.value)} invalid={formErrors.fuelType !== ''}>
                                        <option key="select" value="" disabled>
                                            select...
                                        </option>
                                        {vehicleAssumptions.map((item: IVehicleAssumptions) => {
                                            return (
                                                <option key={item.fuelTypeID} value={item.fuelTypeID}>
                                                    {item.fuelTypeName}
                                                </option>)
                                        })}
                                    </Input>
                                    <FormFeedback>{formErrors.fuelType}</FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row className={`form-group ${errorClass(formErrors.annualUse)}`}>
                                <Label for="annualUse" sm={4} className="formRequired ">Annual Use per Vehicle</Label>
                                <Col sm={8}>
                                    <Input className="fieldWidth100 inlineInput" type="text" name="name" id="annualUse" placeholder="" value={annualUse} onChange={e => setAnnualUse(e.target.value)} maxLength={128} invalid={formErrors.annualUse !== ''} />
                                    <span className="regFont">{annualUseUnits}</span>
                                    <FormFeedback>{formErrors.annualUse}</FormFeedback>
                                </Col>
                            </FormGroup>
                            <hr />
                            <Row row className="buttonRow twoButtons">
                                <Col lg={{ size: 5, order: 2 }} sm={{ size: 12, order: 1 }} className="rightButton">
                                    <Button className="submitButton solidNeg secondaryButton iconRightArrow" onClick={validateAndSaveEditedVehicleSet}>SAVE</Button>
                                </Col>
                                <Col lg={{size:5, order: 1}} sm={{ size: 12, order: 2 }} className="leftButton">
                                    <Button className="cancelButton hollowPos secondaryButton iconReturnArrowBlack" onClick={onVehicleSetEditOrCancel}>CANCEL</Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>
                <Col sm={4} className="formDisplayColumn">
                    <FormHeading title={fleetName === '' ? "My Fleet" : fleetName} textAlign="alignCenter" light={true} />
                    <div className="vehicleSetsSection editForm  formInnerBody">
                        {(currentVehicleSets.length > 0 && currentVehicleSets[0].fleetVehicleSetID > 0) ? (
                            <div>
                                {currentVehicleSets.map((item: IVehicleSet, index: number) => {

                                    return (
                                        <Row key={index}>
                                            <Col className="currentVehicleSet">
                                                <div><strong>{item.vehicleSetName}</strong></div>
                                                <div>{item.vehicleCount}&nbsp;{item.vehicleTypeName}&nbsp;|&nbsp;{item.fuelTypeName}</div>
                                                <div>{item.usageValue}&nbsp;{item.usageValueUnits}</div>
                                            </Col>
                                        </Row>
                                    )
                                })
                                }
                            </div>
                        ) : (
                            <Row>
                                <Col className="currentVehicleSet">
                                    Your vehicle sets will appear here.
                                </Col>
                            </Row>
                        )}
                    </div>
                </Col>
            </Row>
        </Col>
    )
};
